import { useSyncExternalStore } from 'react'

/**
 * Where an empty state's proxied action button stands in the page:
 * - `shown`: a visible button (a page header's "New deal") already offers
 *   the action, so the empty state must not offer it a second time.
 * - `present`: only a click target the viewer can't see — an `aria-hidden`
 *   proxy, or a header button hidden at this viewport — so the empty state
 *   is the visible way in and keeps its action.
 * - `absent`: nothing to click, so a selector-only action would be dead.
 */
export type ProxiedActionState = 'shown' | 'present' | 'absent'

export function readProxiedActionState(
  selector: string | undefined,
): ProxiedActionState {
  if (!selector || typeof document === 'undefined') return 'absent'
  const elements = document.querySelectorAll<HTMLElement>(selector)
  if (elements.length === 0) return 'absent'
  for (const element of elements) {
    if (isShown(element)) return 'shown'
  }
  return 'present'
}

function isShown(element: HTMLElement): boolean {
  // Click-only proxies (the hidden Import buttons) mark themselves hidden.
  if (element.getAttribute('aria-hidden') === 'true') return false
  // jsdom has no checkVisibility; fall back to the hidden attribute.
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility()
  }
  return !element.closest('[hidden]')
}

// Header buttons mount, unmount and toggle after the empty state renders
// (permissions resolve, layout actions portal in, the viewport crosses a
// breakpoint), so re-read on DOM changes, coalesced to one read per frame.
function subscribe(onChange: () => void) {
  let frame = 0
  const schedule = () => {
    if (frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      onChange()
    })
  }
  const observer = new MutationObserver(schedule)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
  })
  window.addEventListener('resize', schedule)
  return () => {
    observer.disconnect()
    window.removeEventListener('resize', schedule)
    cancelAnimationFrame(frame)
  }
}

const subscribeNever = () => () => {}

/** Live {@link ProxiedActionState} of the button `selector` points at. */
export function useProxiedActionState(
  selector: string | undefined,
): ProxiedActionState {
  return useSyncExternalStore(
    selector ? subscribe : subscribeNever,
    () => readProxiedActionState(selector),
    () => 'absent',
  )
}

/** Clicks the first element matching `selector`, if there is one. */
export function clickProxiedAction(selector: string) {
  const element = document.querySelector(selector)
  if (element instanceof HTMLElement) element.click()
}
